import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getLocalItem, getSessionItem, setLocalItem, setSessionItem } from '../utils/safeStorage';

const resolveApiBase = () => {
  const envBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    '';
  if (typeof window === 'undefined') return envBase || '';
  const fallback = window.location.origin || '';
  if (!envBase) return fallback;
  return envBase;
};

const API_BASE = resolveApiBase().replace(/\/$/, '');
const ROLE_ROUTE_TIMEOUT_MS = 6000;

const fetchWithTimeout = async (url, options = {}, timeoutMs = ROLE_ROUTE_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
};

const endpoints = {
  guest: ['/api/guest/me', '/api/auth/customer/me', '/api/customer/me'],
  host: ['/api/auth/host/me'],
  admin: ['/api/admin/auth/me'],
};

const refreshEndpoints = {
  guest: ['/api/auth/customer/refresh', '/api/customer/refresh'],
  host: ['/api/auth/host/refresh'],
  admin: ['/api/admin/auth/refresh'],
};

const accessKeys = {
  guest: 'customerAccessToken',
  host: 'hostAccessToken',
  admin: 'adminAccessToken',
};
const legacyAdminKey = 'hv_admin_token';

const resolveToken = (role, injectedToken = '') => {
  if (injectedToken) return injectedToken;
  if (role === 'guest') {
    return getSessionItem(accessKeys.guest) || getLocalItem(accessKeys.guest) || '';
  }
  if (role === 'host') {
    return getSessionItem(accessKeys.host) || getLocalItem(accessKeys.host) || '';
  }
  if (role === 'admin') {
    return (
      getSessionItem(accessKeys.admin) ||
      getLocalItem(accessKeys.admin) ||
      getSessionItem(legacyAdminKey) ||
      getLocalItem(legacyAdminKey) ||
      ''
    );
  }
  return '';
};

const storeToken = (role, token) => {
  const key = accessKeys[role];
  if (!key || !token) return;
  setSessionItem(key, token);
  setLocalItem(key, token);
  if (role === 'admin') {
    setSessionItem(legacyAdminKey, token);
    setLocalItem(legacyAdminKey, token);
  }
};

const refreshAccessToken = async (role) => {
  const endpointList = refreshEndpoints[role];
  if (!Array.isArray(endpointList) || endpointList.length === 0) return null;
  try {
    for (const endpoint of endpointList) {
      const res = await fetchWithTimeout(`${API_BASE}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status === 404 || res.status === 405) continue;
      if (!res.ok) return null;
      const data = await res.json();
      const nextToken = data?.accessToken || data?.token;
      if (nextToken) storeToken(role, nextToken);
      return nextToken || null;
    }
    return null;
  } catch {
    return null;
  }
};

const RoleRoute = ({ role, redirectTo = '/', children, token: injectedToken = '', validate = true }) => {
  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = resolveToken(role, injectedToken);
    if (!token) {
      setAllowed(false);
      setLoading(false);
      return;
    }
    if (!validate) {
      setAllowed(true);
      setLoading(false);
      return;
    }
    const endpointList = endpoints[role];
    if (!Array.isArray(endpointList) || endpointList.length === 0) {
      setAllowed(false);
      setLoading(false);
      return;
    }
    let active = true;
    const runValidation = async () => {
      try {
        let res = null;
        for (const endpoint of endpointList) {
          const current = await fetchWithTimeout(`${API_BASE}${endpoint}`, {
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'include',
          });
          if (current.status === 404 || current.status === 405) continue;
          res = current;
          break;
        }
        if (!res) {
          setAllowed(false);
          return;
        }

        if (res.status === 401) {
          const refreshed = await refreshAccessToken(role);
          if (refreshed) {
            for (const endpoint of endpointList) {
              const current = await fetchWithTimeout(`${API_BASE}${endpoint}`, {
                headers: { Authorization: `Bearer ${refreshed}` },
                credentials: 'include',
              });
              if (current.status === 404 || current.status === 405) continue;
              res = current;
              break;
            }
          }
        }
        if (!active) return;
        setAllowed(res.ok);
      } catch {
        if (!active) return;
        setAllowed(false);
      } finally {
        if (active) setLoading(false);
      }
    };
    runValidation();
    return () => {
      active = false;
    };
  }, [role, injectedToken, validate]);

  if (loading) {
    return <div className="flex h-screen items-center justify-center text-slate-500">Loading...</div>;
  }
  if (!allowed) {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
};

export default RoleRoute;


